import { useTranslation } from 'react-i18next';
import { Form, Icon, notify, Page } from '@/components';
import { AnnouncementEditPanel } from '@/views/Announcements.Details';
import { Button, Space } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AddAnnouncementArgs,
  useGetAnnouncementDetailsQuery,
  useUpdateAnnouncementMutation,
} from '@/redux/announcements';
import { useCallback } from 'react';
import moment from 'moment';

const useId = () => {
  const params = useParams<'id'>();
  return params.id!;
};

export function AnnouncementEditPage() {
  const { t } = useTranslation();
  const id = useId();
  const title = t('announcements.edit');
  const { data: announcement, isFetching } = useGetAnnouncementDetailsQuery({
    id,
  });
  const [update] = useUpdateAnnouncementMutation();
  const push = useNavigate();

  const handleUpdate = useCallback(
    (value: AddAnnouncementArgs) => {
      update({
        ...value,
        id,
        startDate: value.startDate.utc().toISOString(),
        endDate: value.endDate?.utc().toISOString(),
      })
        .unwrap()
        .then(() => {
          notify.success.t('announcements.details.saved');
          push(-1);
        });
    },
    [id, push, update],
  );

  return (
    <Page htmlTitle={title}>
      <Page.Paths>
        <Page.Path href="/admin#announcements">
          {t('announcements.title')}
        </Page.Path>
        <Page.Path>{title}</Page.Path>
      </Page.Paths>

      <Page.Body>
        <Page.Card
          title={title}
          icon={<Icon type="announcement" />}
          color="#814A93"
          actions={
            <Space size="middle">
              <Button onClick={() => push(-1)}>{t('cancel')}</Button>
              <Form.Submit uid="announcement-details" type="primary">
                {t('save')}
              </Form.Submit>
            </Space>
          }
        >
          {!isFetching && (
            <AnnouncementEditPanel
              onSubmit={handleUpdate}
              initialValue={
                announcement
                  ? {
                      title: announcement.title,
                      body: announcement.body,
                      startDate: moment(announcement.period.start),
                      endDate: announcement.period.end
                        ? moment(announcement.period.end)
                        : null,
                      attachmentIds: [],
                    }
                  : undefined
              }
              id={id}
            />
          )}
        </Page.Card>
      </Page.Body>
    </Page>
  );
}
